/**
 * /lib/table-api.js
 *
 * Thin fetch client for the /table backend surface (T2-landed).
 *
 * Per founder ruling v2.1: /table is a CALLER of the existing /mira
 * pipeline · this client must never invent fields the backend did not
 * return. Every response is passed through verbatim. The skeleton UI
 * renders only what the API ships.
 *
 * FIR-20260612-001 · custom-domain tenant routing fix · API base is
 * resolved at runtime (window.location.origin when served from a
 * linked custom domain · build-time URL when served from the canonical
 * preview host). See lib/backend-url.js for the tenant-Host contract.
 */
import { BACKEND_URL } from "@/lib/backend-url";

const API = BACKEND_URL;

async function jsonOrThrow(res) {
    if (!res.ok) {
        let body = "";
        try { body = await res.text(); } catch { /* noop */ }
        const err = new Error(`HTTP ${res.status}`);
        err.status = res.status;
        err.body = body;
        throw err;
    }
    return res.json();
}

export async function searchTable(q, {
    country, limit = 24, offset = 0, signal,
    cuisine, price_max, price_min, rating_min, route_in, sort,
    intent, open_now,
} = {}) {
    const params = new URLSearchParams({ q, limit: String(limit) });
    if (offset) params.set("offset", String(offset));
    if (country) params.set("country", country);
    if (cuisine) params.set("cuisine", cuisine);
    if (price_max !== undefined && price_max !== null) params.set("price_max", String(price_max));
    if (price_min !== undefined && price_min !== null) params.set("price_min", String(price_min));
    if (rating_min !== undefined && rating_min !== null) params.set("rating_min", String(rating_min));
    if (route_in) params.set("route_in", route_in);
    if (sort) params.set("sort", sort);
    if (intent) params.set("intent", intent);
    if (open_now) params.set("open_now", "true");
    const res = await fetch(`${API}/api/table/search?${params}`, { signal });
    return jsonOrThrow(res);
}

export async function fetchIntents() {
    const res = await fetch(`${API}/api/table/intents`);
    return jsonOrThrow(res);
}

// Founder spec 8 Feb 2026 · top-tab category browse.
// Tabs: all · michelin · fine_dine · bars · nightclubs · destinations.
// No search query · pure archive grid filtered by category.
export async function browseTable({
    tab = "all", city, limit = 24, offset = 0, signal,
    cuisine, price_max, stars, distinction, open_now, authority,
    ranking_max, verification,
} = {}) {
    const params = new URLSearchParams({ tab, limit: String(limit) });
    if (offset) params.set("offset", String(offset));
    if (city) params.set("city", city);
    if (cuisine) params.set("cuisine", cuisine);
    if (price_max) params.set("price_max", price_max);
    if (stars && stars.length) params.set("stars", stars.join(","));
    if (distinction) params.set("distinction", distinction);
    if (open_now) params.set("open_now", "true");
    if (authority) params.set("authority", authority);
    if (ranking_max) params.set("ranking_max", String(ranking_max));
    if (verification) params.set("verification", verification);
    const res = await fetch(`${API}/api/table/browse?${params}`, { signal });
    return jsonOrThrow(res);
}


export async function searchNearbyTable({
    lat, lng, tab = "all", radius_m = 5000, open_now, limit = 24,
} = {}) {
    const params = new URLSearchParams({
        lat: String(lat), lng: String(lng), tab, radius_m: String(radius_m),
        limit: String(limit),
    });
    if (open_now) params.set("open_now", "true");
    const res = await fetch(`${API}/api/table/nearby?${params}`);
    return jsonOrThrow(res);
}


export async function parseTableQuery(q) {
    const params = new URLSearchParams({ q });
    const res = await fetch(`${API}/api/table/parse-query?${params}`);
    return jsonOrThrow(res);
}


// Mira agent · /api/table/agent/plan (founder option c · 9 Feb 2026)
// Returns the full envelope: understanding · ranked candidates ·
// route_plan per candidate (auto_execute flag) · dispatch_preview.
export async function agentPlan({
    q,
    party_size = null,
    when_hint = null,
    location = null,
    member_id = null,
    signal = null,
} = {}) {
    const res = await fetch(`${API}/api/table/agent/plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ q, party_size, when_hint, location, member_id }),
        signal,
    });
    return jsonOrThrow(res);
}

// Persist a Concierge® dispatch (option c · "Send to Concierge®"
// for Tier 4 routes or member-led handoffs even when a Tier 0-3
// deeplink exists).
export async function agentDispatch({
    request_id, chosen_place_id, plan_envelope,
    member_id = null, notes = null,
}) {
    const res = await fetch(`${API}/api/table/agent/dispatch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            request_id, chosen_place_id, plan_envelope, member_id, notes,
        }),
    });
    return jsonOrThrow(res);
}


export async function fetchOffers({ city, card, venue_name } = {}) {
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    if (card) params.set("card", card);
    if (venue_name) params.set("venue_name", venue_name);
    const res = await fetch(`${API}/api/table/offers?${params}`);
    return jsonOrThrow(res);
}

export async function composeCuration({ brief, venue_ids, occasion, locale }) {
    const res = await fetch(`${API}/api/table/curate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ brief, venue_ids, occasion, locale }),
    });
    return jsonOrThrow(res);
}

export async function approveCuration(draft_id) {
    const res = await fetch(`${API}/api/table/curate/${encodeURIComponent(draft_id)}/approve`, {
        method: "POST",
    });
    return jsonOrThrow(res);
}

export async function getVenueDetail(venueId, { signal } = {}) {
    const res = await fetch(
        `${API}/api/table/venue/${encodeURIComponent(venueId)}`,
        { signal },
    );
    return jsonOrThrow(res);
}

export async function resolveBooking(payload, { signal } = {}) {
    const res = await fetch(`${API}/api/table/resolve-booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal,
    });
    return jsonOrThrow(res);
}

export async function conciergeRequest(payload, { signal } = {}) {
    const res = await fetch(`${API}/api/table/concierge-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal,
    });
    return jsonOrThrow(res);
}


// ── E11 · "+ add a verified venue" refine flow ────────────────────
// `discoverMoreForDraft` runs ONE follow-up Places query anchored to
// the draft's destination · returns verified candidates with venues
// already in the draft filtered out.
// `appendPickToDraft` resolves the chosen venue_id, composes ONE
// voice paragraph, appends to the draft. Returns the updated draft.

export async function discoverMoreForDraft(draftId, query, { signal } = {}) {
    const res = await fetch(
        `${API}/api/table/curate/${encodeURIComponent(draftId)}/discover_more`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ query }),
            signal,
        },
    );
    return jsonOrThrow(res);
}


export async function appendPickToDraft(draftId, venueId, { signal } = {}) {
    const res = await fetch(
        `${API}/api/table/curate/${encodeURIComponent(draftId)}/append_pick`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ venue_id: venueId }),
            signal,
        },
    );
    return jsonOrThrow(res);
}

// ── Admin observability (founder ruling 5 Jun 2026 morning) ────────
// Read-only feeds for the /table/admin watch panels. Errors propagate
// so the UI can render an honest fallback (never a fake number).

export async function fetchRouteWatch({ hours = 24, limit = 50 } = {}) {
    const params = new URLSearchParams({
        hours: String(hours),
        limit: String(limit),
    });
    const res = await fetch(`${API}/api/table/admin/route-watch?${params}`);
    return jsonOrThrow(res);
}


export async function fetchDishQueryWatch({ hours = 168, limit = 30 } = {}) {
    const params = new URLSearchParams({
        hours: String(hours),
        limit: String(limit),
    });
    const res = await fetch(`${API}/api/table/admin/dish-query-watch?${params}`);
    return jsonOrThrow(res);
}


// Founder ear-test 5 Jun 2026 night: ping the dish-query observability
// rail on every banner-shown event AND every Concierge® CTA click-
// through. Pure post-hoc analytic · never blocks the member · uses
// sendBeacon when available so the request survives even if the page
// navigates away to WhatsApp.
export function pingDishQuery({ dish, city, source = "wa_clickthrough" }) {
    if (!dish) return;
    const payload = JSON.stringify({
        dish, city: city || null, source,
    });
    const url = `${API}/api/table/admin/dish-query-ping`;
    try {
        if (typeof navigator !== "undefined"
            && typeof navigator.sendBeacon === "function") {
            const blob = new Blob([payload], { type: "application/json" });
            const ok = navigator.sendBeacon(url, blob);
            if (ok) return;
        }
    } catch { /* fall through */ }
    // Fallback · keepalive fetch so the request survives unload
    try {
        fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: payload,
            keepalive: true,
        }).catch(() => {});
    } catch { /* noop */ }
}

export async function fetchCurationWatch({ limit = 50 } = {}) {
    const params = new URLSearchParams({ limit: String(limit) });
    const res = await fetch(`${API}/api/table/admin/curation-watch?${params}`);
    return jsonOrThrow(res);
}


// ── Archive overlay confirm-queue (founder direction 8 Feb 2026) ──
// The desk's on-switch: rows ingested via /admin/archive/upsert_bulk
// land staged with human_confirmed=false. These helpers drive the
// confirm-queue UI that flips the green gate.

export async function fetchArchiveQueue({
    status = "staged",
    category = null,
    whisperOrigin = null,
    readBucket = null,
    limit = 200,
} = {}) {
    const params = new URLSearchParams({
        status, limit: String(limit),
    });
    if (category) params.set("category", category);
    if (whisperOrigin) params.set("whisper_origin", whisperOrigin);
    if (readBucket) params.set("read_bucket", readBucket);
    const res = await fetch(
        `${API}/api/table/admin/archive/queue?${params}`,
    );
    return jsonOrThrow(res);
}


export async function confirmArchiveOverlay(placeId, confirmedBy) {
    const res = await fetch(
        `${API}/api/table/admin/archive/${encodeURIComponent(placeId)}/confirm`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ confirmed_by: confirmedBy }),
        },
    );
    return jsonOrThrow(res);
}


export async function rejectArchiveOverlay(placeId, rejectedBy, reason) {
    const res = await fetch(
        `${API}/api/table/admin/archive/${encodeURIComponent(placeId)}/reject`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                rejected_by: rejectedBy,
                ...(reason ? { reason } : {}),
            }),
        },
    );
    return jsonOrThrow(res);
}

export async function logTableEvent(payload) {
    // fire-and-forget · failures must never block the UX
    try {
        await fetch(`${API}/api/table/log-event`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
    } catch { /* noop */ }
}


// ── Magic Curation · three outputs (post-approval) ────────────────
// Founder rule: nothing runs unless `draft.approved` is True. These
// helpers just hand back URLs / text · the backend honours the gate.

export function dossierUrl(draftId) {
    return `${API}/api/table/curate/${encodeURIComponent(draftId)}/dossier.pdf`;
}


export async function fetchCurationWhatsApp(draftId) {
    const res = await fetch(
        `${API}/api/table/curate/${encodeURIComponent(draftId)}/whatsapp`,
    );
    return jsonOrThrow(res);
}


export async function fetchCurationCollection(draftId) {
    const res = await fetch(
        `${API}/api/table/curate/${encodeURIComponent(draftId)}/collection`,
    );
    return jsonOrThrow(res);
}


export function dossierDocxUrl(draftId) {
    // Magic Curation Phase B3 · editable Word output (founder hand-off
    // 5 Jun 2026 night · same house-format renderer as the locked PDF).
    return `${API}/api/table/curate/${encodeURIComponent(draftId)}/dossier.docx`;
}


// Magic Curation Phase B1/B2 · brief-driven discovery (5 Jun 2026 night).
// Doctrine: the model parses the brief; the v2 pipeline finds the
// venues. Two thin endpoints chained by the studio.
export async function parseCurationBrief(brief) {
    const res = await fetch(`${API}/api/table/curate/brief`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ brief }),
    });
    return jsonOrThrow(res);
}


export async function discoverFromBrief(parsed, opts = {}) {
    const res = await fetch(`${API}/api/table/curate/discover`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            parsed,
            per_query_limit: opts.per_query_limit || 8,
            slate_cap: opts.slate_cap || 12,
        }),
    });
    return jsonOrThrow(res);
}


export async function sendCurationWhatsApp(draftId, { to_phone, public_url }) {
    const params = new URLSearchParams({ to_phone });
    if (public_url) params.set("public_url", public_url);
    const res = await fetch(
        `${API}/api/table/curate/${encodeURIComponent(draftId)}/send-whatsapp?${params}`,
        { method: "POST" },
    );
    return jsonOrThrow(res);
}



/**
 * Universal click-intent · founder ruling 16 Feb 2026 PM-22.
 * Mirrors enjoy/api.js#logIntent · fire-and-forget, keepalive,
 * swallow errors. NEVER blocks the deep-link opening.
 * See /app/memory/TELEMETRY_DOCTRINE.md
 *
 * payload: { rail, entity_id, entity_snapshot, intent_type? }
 */
export function logTableIntent(payload) {
    return fetch(`${API}/api/table/intent`, {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    }).catch(() => {});
}
